import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { FileCheck2, Save, FileText, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';

const FIELDS = [
  { key: 'job_type',         label: 'Job Type',             placeholder: 'Repairs and Recoat Maintenance', type: 'text' },
  { key: 'completion_date',  label: 'Completed On',         placeholder: 'Monday 11th May 2026',          type: 'text' },
  { key: 'certificate_date', label: 'Certificate Date',     placeholder: '13th May 2026',                  type: 'text' },
  { key: 'authorized_by',    label: 'Authorized By',        placeholder: 'Trevor Woolford',                type: 'text' },
];

const TEXTAREAS = [
  { key: 'repairs',        label: 'Repairs',     hint: 'Single paragraph — describe ridges, tiles, valleys, flashings, washes etc.' },
  { key: 'restoration',    label: 'Restoration', hint: 'Single paragraph — sealer, primer, colour coats, warranty years.' },
  { key: 'gutters',        label: 'Gutters',     hint: 'Single paragraph — guttering replacements / repairs (or leave blank).' },
];

const DEFAULT_BENEFITS = [
  'Five Year Workmanship Warranty*',
  'Yearly Discounted Maintenance Schedule*',
  'Support and Back up for all your roofing requirements',
];

export default function CompletionCertificateCard({ appraisalId, status, onOpenPdf }) {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [cert, setCert] = useState({});
  const [benefitsText, setBenefitsText] = useState(DEFAULT_BENEFITS.join('\n'));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/appraisals/${appraisalId}`);
      const existing = r.data?.completion_certificate || {};
      setCert(existing);
      const benefits = Array.isArray(existing.benefits) && existing.benefits.length > 0 ? existing.benefits : DEFAULT_BENEFITS;
      setBenefitsText(benefits.join('\n'));
    } catch { /* ignore */ }
  }, [api, appraisalId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleField = (k, v) => { setCert(c => ({ ...c, [k]: v })); setSaved(false); };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const payload = { ...cert };
      const benefitsArr = benefitsText.split('\n').map(s => s.trim()).filter(Boolean);
      if (benefitsArr.length) payload.benefits = benefitsArr;
      await api.patch(`/appraisals/${appraisalId}/completion-certificate`, payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      console.error('Save Completion Certificate failed', e);
      alert('Failed to save certificate. Please try again.');
    }
    setSaving(false);
  };

  const handleReset = () => {
    if (!window.confirm('Clear all certificate fields back to defaults? The PDF will fall back to today\u2019s date + the standard scope-of-works text.')) return;
    setCert({});
    setBenefitsText(DEFAULT_BENEFITS.join('\n'));
    setSaved(false);
  };

  const isPostCompletion = ['Completed', 'Paid', 'Closed', 'Invoiced', 'Overdue'].includes(status || '');

  return (
    <div data-testid="completion-certificate-card" className="rounded-lg border border-[#E2E2DF] bg-white overflow-hidden">
      <button
        type="button"
        data-testid="completion-certificate-toggle"
        onClick={() => setOpen(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3 text-left">
          <div className="w-9 h-9 rounded-md bg-emerald-50 text-[#0F4F1B] flex items-center justify-center">
            <FileCheck2 size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Certificate of Completion</h3>
            <p className="text-xs text-slate-500">
              {isPostCompletion ? 'Ready to issue.' : 'Pre-fill now so it\u2019s ready when the job is paid.'} Hand to client after final payment.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isPostCompletion && <span className="text-[10px] uppercase font-bold tracking-[0.15em] text-slate-400">Optional</span>}
          {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-[#E2E2DF] p-4 space-y-4 bg-slate-50/40">
          {/* Top row — single-line fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FIELDS.map(f => (
              <label key={f.key} className="block">
                <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">{f.label}</span>
                <input
                  type={f.type}
                  data-testid={`cert-${f.key}`}
                  value={cert[f.key] || ''}
                  onChange={(e) => handleField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4F1B]/20 focus:border-[#0F4F1B]"
                />
              </label>
            ))}
          </div>

          {/* Work-completed textareas */}
          <div className="space-y-3">
            {TEXTAREAS.map(t => (
              <label key={t.key} className="block">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{t.label}</span>
                  <span className="text-[10px] text-slate-400 italic">{t.hint}</span>
                </div>
                <textarea
                  data-testid={`cert-${t.key}`}
                  value={cert[t.key] || ''}
                  onChange={(e) => handleField(t.key, e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4F1B]/20 focus:border-[#0F4F1B] resize-none"
                />
              </label>
            ))}
          </div>

          {/* Benefits & note */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="md:col-span-2 block">
              <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">Benefits (one per line)</span>
              <textarea
                data-testid="cert-benefits"
                value={benefitsText}
                onChange={(e) => { setBenefitsText(e.target.value); setSaved(false); }}
                rows={3}
                className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4F1B]/20 focus:border-[#0F4F1B] resize-none"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">Conditions Note</span>
              <input
                data-testid="cert-conditions_note"
                value={cert.conditions_note ?? '* Conditions Apply'}
                onChange={(e) => handleField('conditions_note', e.target.value)}
                className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4F1B]/20 focus:border-[#0F4F1B]"
              />
            </label>
          </div>

          {/* Action bar */}
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#E2E2DF]">
            <button
              type="button"
              data-testid="cert-reset-btn"
              onClick={handleReset}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md text-slate-500 hover:bg-slate-200/60 transition-colors"
            >
              <RotateCcw size={13} />Reset to defaults
            </button>
            <div className="flex items-center gap-2">
              {saved && <span className="text-xs text-emerald-700 font-semibold">Saved ✓</span>}
              <button
                type="button"
                data-testid="cert-save-btn"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#0F4F1B] text-white hover:bg-[#0a3812] transition-colors disabled:opacity-50"
              >
                <Save size={13} />{saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                data-testid="cert-preview-btn"
                onClick={() => onOpenPdf?.('certificate-of-completion')}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#0F4F1B] text-[#0F4F1B] hover:bg-[#0F4F1B] hover:text-white transition-colors"
              >
                <FileText size={13} />Preview PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
